import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";

import LocationMappingInfo from "component/YardMapDialog/LocationMappingInfo";
import YardMap from "component/YardMapDialog/YardMap";
import LocationsValidationAlert from "./LocationsValidationAlert";
import DeleteYardMapButton from "./DeleteYardMapButton";
import { clearErrorEditingYardMap, editYardMap } from "store";
import { keys } from "i18n";

export default function MapContainer({ yardId }) {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const { t } = useTranslation();

  const secrets = useSelector((state) => state.secrets);
  const { label, parkingLocations } = useSelector((state) => state.yardConfigData)[yardId];
  const yardMapData = useSelector((state) => state.yardMapData);
  const permissionsData = useSelector((state) => state.permissionsData);

  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedLocation) {
      setSelectedLocation(
        yardMapData.yardMap.locations.find(
          ({ locationCode }) => locationCode === selectedLocation.locationCode
        )
      );
    }
  }, [yardMapData.yardMap]);

  const selectLocation = (selectedLocation) => setSelectedLocation(selectedLocation);
  const autoSelectLocationWithChangedCode = (locationCode) => setSelectedLocation({ locationCode });

  const handleEditYardMap = (edits, callback) => dispatch(editYardMap(edits, callback));
  const handleClearErrorEditingYardMap = () => dispatch(clearErrorEditingYardMap());

  // Same height as the map defined in styles.scss: 100vh - 270px
  // The 270px offset is to account for TCP header and footer and Yard Designer header
  // If there's a need to change, need to change both together
  const dialogHeight = window.innerHeight - 270;

  return (
    <Column>
      <Row alignmentHorizontal="justify">
        <Text type="h200">{t(keys.YARD_MAP_MANAGEMENT)}</Text>
        <DeleteYardMapButton yardId={yardId} version={yardMapData.yardMap.version} />
      </Row>

      <LocationsValidationAlert yardId={yardId} />

      <Row widths={["fill", "400px"]} alignmentVertical="top" spacing="none">
        <YardMap
          secrets={secrets}
          dialogHeight={dialogHeight}
          yardLabel={label}
          yardMapData={yardMapData}
          parkingLocations={parkingLocations}
          selectedLocation={selectedLocation}
          selectLocation={selectLocation}
          editYardMap={handleEditYardMap}
        />

        <LocationMappingInfo
          dialogHeight={dialogHeight}
          yardMapData={yardMapData}
          selectedLocation={selectedLocation}
          locations={parkingLocations}
          editYardMap={handleEditYardMap}
          autoSelectLocationWithChangedCode={autoSelectLocationWithChangedCode}
          clearErrorEditingYardMap={handleClearErrorEditingYardMap}
          permissionsData={permissionsData}
        />
      </Row>
    </Column>
  );
}

MapContainer.propTypes = {
  yardId: PropTypes.string.isRequired,
};
